// src/modules/app-views/crm/pages/Kanban/modals/TransferOpportunityModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo, useState } from 'react';
import { Col, Form, Row, Select } from 'antd';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const Step1 = ({ currentBoardUid }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & CONTEXTS
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext();
  const storedParameters = useZustand('app', 'parameters');
  const storedTeam = useZustand('auth', 'team');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boards = useMemo(
    () => (storedParameters?.modules?.crm?.boards || []).filter(b => !b.removedAt && !b.archivedAt),
    [storedParameters?.modules?.crm?.boards]
  );
  const boardOptions = useMemo(
    () => boards.map(b => ({
      value: b.uid,
      label: b.title,
      text: b.title,
      disabled: b.uid === currentBoardUid,
    })),
    [boards, currentBoardUid]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedBoardUid, setSelectedBoardUid] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II (depend on selectedBoardUid)
  // ─────────────────────────────────────────────────────────────────────────────
  const selectedBoard = useMemo(
    () => boards.find(b => b.uid === selectedBoardUid),
    [boards, selectedBoardUid]
  );

  const columnOptions = useMemo(() => {
    if (!selectedBoard?.columns) return [];
    return [...selectedBoard.columns]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(c => ({ value: c.uid, label: c.title, text: c.title }));
  }, [selectedBoard]);

  const consultantOptions = useMemo(() => {
    const teamAccessIds = selectedBoard?.teamAccessIds || [];
    const activeMembers = (storedTeam || []).filter(m => m.status === 2);
    const filtered = teamAccessIds.length > 0
      ? activeMembers.filter(m => teamAccessIds.includes(m._id || m.accountId))
      : activeMembers;

    return filtered.map(m => ({
      value: m._id || m.accountId,
      label: m.name || m.fullName || '',
      text: m.name || m.fullName || '',
      avatarSrc: getAvatarSrc(m),
      backColor: m.backColor || '#e6f4ff',
      foreColor: m.foreColor || '#0958d9',
    }));
  }, [selectedBoard, storedTeam]);

  const hasSelection = !!selectedBoardUid;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleBoardChange = useCallback(value => {
    setSelectedBoardUid(value);
    // Sync to form + reset dependent fields when board changes
    form.setFieldsValue({ boardUid: value, columnUid: undefined, consultantId: undefined });
    // Clear validation errors on dependent fields
    form.setFields([
      { name: 'columnUid', errors: [] },
      { name: 'consultantId', errors: [] },
    ]);
  }, [form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-2">
      {/* Board selector */}
      <Form.Item
        name="boardUid"
        label={translateX('board')}
        rules={[{ required: true, message: () => translateX('required_field') }]}
      >
        <Select
          value={selectedBoardUid}
          onChange={handleBoardChange}
          options={boardOptions}
          placeholder={translateX('select_board')}
          showSearch
          filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
          autoFocus
        />
      </Form.Item>

      {/* Column selector */}
      <Form.Item
        name="columnUid"
        label={translateX('column')}
        rules={[{ required: true, message: () => translateX('required_field') }]}
      >
        <Select
          options={columnOptions}
          placeholder={translateX('select_column')}
          disabled={!hasSelection}
          showSearch
          filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      {/* Consultant selector */}
      <Form.Item
        name="consultantId"
        label={translateX('responsible_consultant')}
        rules={[{ required: true, message: () => translateX('required_field') }]}
      >
        <Select
          options={consultantOptions}
          placeholder={translateX('select_consultant')}
          disabled={!hasSelection}
          showSearch
          filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
          optionRender={option => {
            const { avatarSrc, backColor, foreColor, label } = option.data;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SimpleAvatar
                  size={24}
                  src={avatarSrc}
                  name={label}
                  backColor={backColor}
                  foreColor={foreColor}
                  showBorder={false}
                  showShadow={false}
                />
                <span>{label}</span>
              </div>
            );
          }}
        />
      </Form.Item>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default Step1;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAvatarSrc(member) {
  const avatar = member?.avatar;
  return avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl || member?.img || null;
}
